import React, { useEffect, useState } from 'react';
import { Sidebar } from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Plus, Edit, Trash2, Power, Eye, EyeOff } from 'lucide-react';

export default function CollegeManagement() {
  const { getAuthHeaders } = useAuth();
  const [colleges, setColleges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCollege, setEditingCollege] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [showInactive, setShowInactive] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    courses: '',
    specializations: '',
    sessions: '',
    admission_types: ''
  });

  const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    fetchColleges();
  }, []);

  const fetchColleges = async () => {
    try {
      // Admin/Sub-Admin fetch all colleges including inactive ones for management
      const response = await axios.get(`${API_URL}/colleges?include_inactive=true`, {
        headers: getAuthHeaders()
      });
      setColleges(response.data);
    } catch (error) {
      toast.error('Failed to fetch colleges');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (college) => {
    setTogglingId(college.id);
    try {
      await axios.put(`${API_URL}/colleges/${college.id}/toggle-status`, {}, {
        headers: getAuthHeaders()
      });
      toast.success(`College ${college.is_active ? 'disabled' : 'enabled'} successfully`);
      fetchColleges();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle college status');
    } finally {
      setTogglingId(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const coursesArray = formData.courses.split(',').map(c => c.trim());
      let specializationsObj = {};
      
      try {
        specializationsObj = JSON.parse(formData.specializations);
      } catch (error) {
        toast.error('Invalid specializations format. Use JSON format like: {"Diploma":["CS","IT"],"UG":["B.A.","B.Sc."]}');
        return;
      }

      const payload = {
        name: formData.name,
        location: formData.location,
        courses: coursesArray,
        specializations: specializationsObj,
        sessions: formData.sessions.split(',').map(s => s.trim()),
        admission_types: formData.admission_types.split(',').map(a => a.trim())
      };

      if (editingCollege) {
        await axios.put(`${API_URL}/colleges/${editingCollege.id}`, payload, {
          headers: getAuthHeaders()
        });
        toast.success('College updated successfully');
      } else {
        await axios.post(`${API_URL}/colleges`, payload, {
          headers: getAuthHeaders()
        });
        toast.success('College created successfully');
      }

      setIsDialogOpen(false);
      resetForm();
      fetchColleges();
    } catch (error) {
      toast.error('Failed to save college');
    }
  };

  const handleEdit = (college) => {
    setEditingCollege(college);
    setFormData({
      name: college.name,
      location: college.location,
      courses: college.courses.join(', '),
      specializations: JSON.stringify(college.specializations),
      sessions: college.sessions.join(', '),
      admission_types: college.admission_types.join(', ')
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this college?')) return;
    
    try {
      await axios.delete(`${API_URL}/colleges/${id}`, {
        headers: getAuthHeaders()
      });
      toast.success('College deleted successfully');
      fetchColleges();
    } catch (error) {
      toast.error('Failed to delete college');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      location: '',
      courses: '',
      specializations: '',
      sessions: '',
      admission_types: ''
    });
    setEditingCollege(null);
  };

  return (
    <Sidebar>
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              College Management
            </h1>
            <p className="text-slate-600 mt-2">Manage universities and colleges</p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button data-testid="add-college-button" className="bg-[#0052CC] hover:bg-[#0043A8] text-white">
                <Plus className="w-4 h-4 mr-2" />
                Add College
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingCollege ? 'Edit College' : 'Add New College'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name">College Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                    data-testid="college-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="location">Location</Label>
                  <Input
                    id="location"
                    value={formData.location}
                    onChange={(e) => setFormData({...formData, location: e.target.value})}
                    required
                    data-testid="college-location-input"
                  />
                </div>
                <div>
                  <Label htmlFor="courses">Courses (comma-separated)</Label>
                  <Input
                    id="courses"
                    value={formData.courses}
                    onChange={(e) => setFormData({...formData, courses: e.target.value})}
                    placeholder="Diploma, UG, PG"
                    required
                    data-testid="college-courses-input"
                  />
                </div>
                <div>
                  <Label htmlFor="specializations">Specializations (JSON format)</Label>
                  <Input
                    id="specializations"
                    value={formData.specializations}
                    onChange={(e) => setFormData({...formData, specializations: e.target.value})}
                    placeholder='{"Diploma":["CS","IT"],"UG":["B.A.","B.Sc."]}'
                    required
                    data-testid="college-specializations-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">Example: {`{"Diploma":["CS","IT"],"UG":["B.A.","B.Sc."]}`}</p>
                </div>
                <div>
                  <Label htmlFor="sessions">Sessions (comma-separated)</Label>
                  <Input
                    id="sessions"
                    value={formData.sessions}
                    onChange={(e) => setFormData({...formData, sessions: e.target.value})}
                    placeholder="2024-25, 2025-26"
                    required
                    data-testid="college-sessions-input"
                  />
                </div>
                <div>
                  <Label htmlFor="admission_types">Admission Types (comma-separated)</Label>
                  <Input
                    id="admission_types"
                    value={formData.admission_types}
                    onChange={(e) => setFormData({...formData, admission_types: e.target.value})}
                    placeholder="Regular, Distance, Part-Time"
                    required
                    data-testid="college-admission-types-input"
                  />
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" data-testid="college-submit-button" className="bg-[#0052CC] hover:bg-[#0043A8] text-white">
                    {editingCollege ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="text-center py-12">Loading colleges...</div>
        ) : (
          <>
            {/* Filter Section */}
            <div className="mb-4 flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Switch
                  id="show-inactive"
                  checked={showInactive}
                  onCheckedChange={setShowInactive}
                  data-testid="show-inactive-toggle"
                />
                <Label htmlFor="show-inactive" className="text-sm text-slate-600 cursor-pointer">
                  Show disabled colleges
                </Label>
              </div>
              <div className="text-sm text-slate-500">
                {colleges.filter(c => c.is_active !== false).length} active, {colleges.filter(c => c.is_active === false).length} disabled
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
              <table className="w-full" data-testid="colleges-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider">Status</th>
                    <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider">Name</th>
                    <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider">Location</th>
                    <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider">Courses</th>
                    <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {colleges
                    .filter(college => showInactive || college.is_active !== false)
                    .map((college) => (
                    <tr 
                      key={college.id} 
                      className={`border-b border-slate-100 hover:bg-slate-50/50 ${college.is_active === false ? 'bg-slate-50/80' : ''}`} 
                      data-testid={`college-row-${college.id}`}
                    >
                      <td className="p-4 align-middle text-sm">
                        <div className="flex items-center gap-2">
                          {college.is_active !== false ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                              <Eye className="w-3 h-3" />
                              Active
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-slate-200 text-slate-600">
                              <EyeOff className="w-3 h-3" />
                              Disabled
                            </span>
                          )}
                        </div>
                      </td>
                      <td className={`p-4 align-middle text-sm font-medium ${college.is_active === false ? 'text-slate-400' : 'text-slate-700'}`}>
                        {college.name}
                      </td>
                      <td className={`p-4 align-middle text-sm ${college.is_active === false ? 'text-slate-400' : 'text-slate-600'}`}>
                        {college.location}
                      </td>
                      <td className={`p-4 align-middle text-sm ${college.is_active === false ? 'text-slate-400' : 'text-slate-600'}`}>
                        {college.courses.slice(0, 3).join(', ')}{college.courses.length > 3 ? '...' : ''}
                      </td>
                      <td className="p-4 align-middle text-sm">
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleToggleStatus(college)}
                            disabled={togglingId === college.id}
                            data-testid={`toggle-college-${college.id}`}
                            className={college.is_active !== false ? 'text-amber-600 hover:text-amber-700 hover:bg-amber-50' : 'text-green-600 hover:text-green-700 hover:bg-green-50'}
                            title={college.is_active !== false ? 'Disable college' : 'Enable college'}
                          >
                            <Power className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEdit(college)}
                            data-testid={`edit-college-${college.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDelete(college.id)}
                            data-testid={`delete-college-${college.id}`}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {colleges.length === 0 && (
                <div className="text-center py-12 text-slate-500">
                  No colleges found. Add your first college to get started.
                </div>
              )}
              {colleges.length > 0 && colleges.filter(college => showInactive || college.is_active !== false).length === 0 && (
                <div className="text-center py-12 text-slate-500">
                  No active colleges to display. Toggle "Show disabled colleges" to see all.
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Sidebar>
  );
}